import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import {
  type ApprovalItemRecord,
  resolveApprovalForHash,
} from '@/lib/content-doctrine/article-approval';
import type { ArticleApprovalState } from '@/lib/content-doctrine/article-progress';
import type {
  PublicationStage,
  PublicationWorkingStage,
} from '@/lib/content-doctrine/article-publication-stage';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

export type ArticleReviewState = 'not_sent' | 'waiting' | 'approved' | 'needs_changes';
export type ExternalPublicationState = PublicationStage | 'cancelled';

export interface ArticlePublicationSummary {
  state: Exclude<ExternalPublicationState, 'cancelled'>;
  scheduledAt: string | null;
  publishedAt: string | null;
  publishedUrl: string | null;
  contentHash: string | null;
  approvalBasis: string | null;
  attestationReason: string | null;
}

export interface ArticleHandoffState {
  taskId: string;
  projectId: string;
  reviewState: ArticleReviewState;
  approvalDecisionId: string | null;
  publicationId: string | null;
  publicationState: ExternalPublicationState | null;
  scheduledAt: string | null;
  publishedAt: string | null;
  publishedUrl: string | null;
  attestationReason: 'approved_outside_mora' | 'client_review_not_required' | null;
}

const REVIEW_STATE_FOR: Record<ArticleApprovalState, ArticleReviewState> = {
  approved: 'approved',
  changes: 'needs_changes',
  waiting: 'waiting',
  stale: 'not_sent',
  not_sent: 'not_sent',
};

interface RawApprovalItem {
  id: string;
  request_id: string;
  created_at: string;
  snapshot_hash: string;
  snapshot: unknown;
}

interface RawApprovalDecision {
  id: string;
  item_id: string;
  verdict: string;
  item_snapshot_hash: string | null;
  created_at: string;
}

function articleRefHash(snapshot: unknown): string | null {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const ref = (snapshot as { articleRef?: unknown }).articleRef;
  if (!ref || typeof ref !== 'object') return null;
  const hash = (ref as { contentHash?: unknown }).contentHash;
  return typeof hash === 'string' && hash ? hash : null;
}

function buildApprovalRecords(
  items: readonly RawApprovalItem[],
  requests: ReadonlyArray<{ id: string; status: string }>,
  decisions: readonly RawApprovalDecision[],
): ApprovalItemRecord[] {
  const requestStatus = new Map(requests.map((request) => [request.id, request.status]));
  return items.map((item) => {
    const decision = decisions
      .filter(
        (candidate) =>
          candidate.item_id === item.id && candidate.item_snapshot_hash === item.snapshot_hash,
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    return {
      itemId: item.id,
      requestId: item.request_id,
      createdAt: item.created_at,
      refHash: articleRefHash(item.snapshot),
      requestStatus: requestStatus.get(item.request_id) ?? null,
      decisionId: decision?.id ?? null,
      verdict: decision?.verdict ?? null,
    };
  });
}

export const articleHandoffKey = (taskId: string | null, contentHash?: string | null) =>
  ['article-handoff', taskId, contentHash ?? ''] as const;

export function useArticleHandoffState(taskId: string | null, contentHash?: string | null) {
  const { user } = useAuth();
  return useQuery<ArticleHandoffState | null>({
    queryKey: articleHandoffKey(taskId, contentHash),
    enabled: Boolean(taskId && user?.id),
    queryFn: async () => {
      const { data: task, error: taskError } = await supabase
        .from('project_tasks')
        .select('id, project_id')
        .eq('id', taskId as string)
        .maybeSingle();
      if (taskError) throw new Error(taskError.message);
      if (!task) return null;

      const [{ data: items, error: itemError }, { data: publications, error: publicationError }] =
        await Promise.all([
          supabase
            .from('approval_items')
            .select('id, request_id, created_at, snapshot_hash, snapshot')
            .eq('project_task_id', task.id)
            .order('created_at', { ascending: false })
            .limit(50),
          supabase
            .from('external_publications')
            .select(
              'id, state, scheduled_at, published_at, published_url, attestation_reason, blocked_at',
            )
            .eq('project_task_id', task.id)
            .eq('content_hash', contentHash ?? '')
            .neq('state', 'cancelled')
            .order('created_at', { ascending: false })
            .limit(1),
        ]);
      if (itemError) throw new Error(itemError.message);
      if (publicationError) throw new Error(publicationError.message);

      let reviewState: ArticleReviewState = 'not_sent';
      let approvalDecisionId: string | null = null;
      const itemRows = items ?? [];
      if (itemRows.length > 0) {
        const requestIds = [...new Set(itemRows.map((item) => item.request_id))];
        const [{ data: requests, error: requestError }, { data: decisions, error: decisionError }] =
          await Promise.all([
            supabase.from('approval_requests').select('id, status').in('id', requestIds),
            supabase
              .from('approval_decisions')
              .select('id, item_id, verdict, item_snapshot_hash, created_at')
              .in(
                'item_id',
                itemRows.map((item) => item.id),
              )
              .order('created_at', { ascending: false }),
          ]);
        if (requestError) throw new Error(requestError.message);
        if (decisionError) throw new Error(decisionError.message);
        const records = buildApprovalRecords(itemRows, requests ?? [], decisions ?? []);
        const resolved = resolveApprovalForHash(records, contentHash ?? null);
        approvalDecisionId = resolved.decisionId;
        reviewState = REVIEW_STATE_FOR[resolved.state];
      }

      const publication = publications?.[0] ?? null;
      return {
        taskId: task.id,
        projectId: task.project_id,
        reviewState,
        approvalDecisionId,
        publicationId: publication?.id ?? null,
        publicationState: (publication?.state as ExternalPublicationState | undefined) ?? null,
        scheduledAt: publication?.scheduled_at ?? null,
        publishedAt: publication?.published_at ?? null,
        publishedUrl: publication?.published_url ?? null,
        attestationReason:
          publication?.attestation_reason === 'approved_outside_mora' ||
          publication?.attestation_reason === 'client_review_not_required'
            ? publication.attestation_reason
            : null,
      };
    },
  });
}

/**
 * The latest non-cancelled publication per topic, for every article on a page
 * at once — the same "one query, not N" shape as `useEditorialArticleScores`.
 *
 * Unlike `useArticleHandoffState`, this does NOT pin the result to one
 * content hash: a summary badge answers "has this topic been handed off",
 * and a revision landing after the handoff is the dialog's job to surface,
 * not a reason to hide the handoff from the table.
 */
export function useArticlePublicationStates(taskIds: readonly string[]) {
  const { user } = useAuth();
  const ids = [...new Set(taskIds)].sort();
  return useQuery<Map<string, ArticlePublicationSummary>>({
    queryKey: ['article-publications', ids],
    enabled: Boolean(user?.id) && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('external_publications')
        .select(
          'project_task_id, state, scheduled_at, published_at, published_url, content_hash, approval_basis, attestation_reason, created_at',
        )
        .in('project_task_id', ids)
        .neq('state', 'cancelled')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      const out = new Map<string, ArticlePublicationSummary>();
      for (const row of data ?? []) {
        // Ordered newest-first, so the first row seen per task is the latest.
        if (out.has(row.project_task_id)) continue;
        out.set(row.project_task_id, {
          state: row.state as ArticlePublicationSummary['state'],
          scheduledAt: row.scheduled_at,
          publishedAt: row.published_at,
          publishedUrl: row.published_url,
          contentHash: row.content_hash,
          approvalBasis: row.approval_basis,
          attestationReason: row.attestation_reason,
        });
      }
      return out;
    },
  });
}

export function useArticleApprovalStates(
  taskIds: readonly string[],
  hashByTopicId: ReadonlyMap<string, string | null>,
) {
  const { user } = useAuth();
  const idKey = [...new Set(taskIds)].sort().join(',');
  const ids = useMemo(() => (idKey ? idKey.split(',') : []), [idKey]);
  const query = useQuery<Map<string, ApprovalItemRecord[]>>({
    queryKey: ['article-approvals', ids],
    enabled: Boolean(user?.id) && ids.length > 0,
    queryFn: async () => {
      const { data: items, error: itemError } = await supabase
        .from('approval_items')
        .select('id, project_task_id, request_id, created_at, snapshot_hash, snapshot')
        .in('project_task_id', ids)
        .order('created_at', { ascending: false });
      if (itemError) throw new Error(itemError.message);
      const itemRows = items ?? [];
      const out = new Map<string, ApprovalItemRecord[]>();
      if (itemRows.length === 0) return out;

      const requestIds = [...new Set(itemRows.map((item) => item.request_id))];
      const [{ data: requests, error: requestError }, { data: decisions, error: decisionError }] =
        await Promise.all([
          supabase.from('approval_requests').select('id, status').in('id', requestIds),
          supabase
            .from('approval_decisions')
            .select('id, item_id, verdict, item_snapshot_hash, created_at')
            .in(
              'item_id',
              itemRows.map((item) => item.id),
            )
            .order('created_at', { ascending: false }),
        ]);
      if (requestError) throw new Error(requestError.message);
      if (decisionError) throw new Error(decisionError.message);

      const records = buildApprovalRecords(itemRows, requests ?? [], decisions ?? []);
      itemRows.forEach((item, index) => {
        const record = records[index];
        if (!record || !item.project_task_id) return;
        const list = out.get(item.project_task_id) ?? [];
        list.push(record);
        out.set(item.project_task_id, list);
      });
      return out;
    },
  });

  const records = query.data;
  const states = useMemo(() => {
    const out = new Map<string, ArticleApprovalState>();
    if (!records) return out;
    for (const id of ids) {
      out.set(
        id,
        resolveApprovalForHash(records.get(id) ?? [], hashByTopicId.get(id) ?? null).state,
      );
    }
    return out;
  }, [records, hashByTopicId, ids]);

  return { ...query, data: records ? states : undefined };
}

export type ArticleHandoffUpdate =
  | {
      mode: 'ready';
      /** Which stage after the hand-off the article is in. Omitted: scheduled when a date is given, else handed off. */
      stage?: PublicationWorkingStage;
      scheduledDate?: string | null;
      reviewExemptionReason?: 'approved_outside_mora' | 'client_review_not_required' | null;
      approvalDecisionId?: string | null;
    }
  | {
      mode: 'published';
      publishedUrl: string;
      scheduledDate?: string | null;
      reviewExemptionReason?: 'approved_outside_mora' | 'client_review_not_required' | null;
      approvalDecisionId?: string | null;
    }
  | { mode: 'clear' };

function dateToIso(date: string): string {
  return new Date(`${date}T12:00:00`).toISOString();
}

export interface ArticleHandoffIdentity {
  workspaceId: string;
  taskId: string;
  messageId: string;
  documentId: string;
  contentHash: string;
}

export function useUpdateArticleHandoff(identity: ArticleHandoffIdentity | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ArticleHandoffUpdate) => {
      if (!identity || !user?.id) throw new Error('This article is unavailable.');
      if (input.mode === 'clear') {
        const { error } = await supabase
          .from('external_publications')
          .update({ state: 'cancelled' })
          .eq('project_task_id', identity.taskId)
          .eq('content_hash', identity.contentHash)
          .neq('state', 'cancelled');
        if (error) throw new Error(error.message);
        return;
      }

      const ownerId = await resolveOwnerId(user.id);
      const scheduledAt = input.scheduledDate ? dateToIso(input.scheduledDate) : null;
      const approvalDecisionId = input.approvalDecisionId ?? null;
      const approvalBasis = approvalDecisionId ? 'client_decision' : 'operator_attestation';
      if (!approvalDecisionId && !input.reviewExemptionReason) {
        throw new Error('Record why Content Desk review is not required.');
      }
      const state: PublicationStage =
        input.mode === 'published'
          ? 'published'
          : (input.stage ?? (scheduledAt ? 'scheduled' : 'handed_off'));
      if (state === 'scheduled' && !scheduledAt) {
        throw new Error('Choose the expected publish date.');
      }
      const now = new Date().toISOString();
      const row = {
        owner_id: ownerId,
        workspace_id: identity.workspaceId,
        project_task_id: identity.taskId,
        source_message_id: identity.messageId,
        source_document_id: identity.documentId,
        content_hash: identity.contentHash,
        state,
        approval_basis: approvalBasis,
        approval_decision_id: approvalDecisionId,
        attestation_reason: approvalDecisionId ? null : input.reviewExemptionReason,
        attested_by: approvalDecisionId ? null : user.id,
        attested_at: approvalDecisionId ? null : now,
        handed_off_by: user.id,
        scheduled_at: scheduledAt,
        published_at: input.mode === 'published' ? now : null,
        published_url: input.mode === 'published' ? input.publishedUrl.trim() : null,
        blocked_at: null,
        blocked_reason: null,
      };
      const { data: existing, error: existingError } = await supabase
        .from('external_publications')
        .select('id')
        .eq('project_task_id', identity.taskId)
        .eq('content_hash', identity.contentHash)
        .neq('state', 'cancelled')
        .maybeSingle();
      if (existingError) throw new Error(existingError.message);
      // Moving along a stage keeps WHEN it was handed off (the column defaults to now() on insert and is not
      // rewritten here); `handed_off_by` is still set because the table's RLS check requires it to be the caller.
      const { error } = existing
        ? await supabase.from('external_publications').update(row).eq('id', existing.id)
        : await supabase.from('external_publications').insert(row);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      for (const queryKey of [
        articleHandoffKey(identity?.taskId ?? null, identity?.contentHash),
        ['editorial-map'],
        // The bulk reader behind the Editorial table's and the Map's status
        // badges — `['external-publications']` invalidated nothing real; no
        // query in the app is ever keyed that way.
        ['article-publications'],
        ['article-approvals'],
        ['desk'],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
